<script>
export default {
  props: {
    vesselGroupsArray: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      success: false,
      error: false,
      activeGroupId: null,
    };
  },
  mounted() {
    // Check for vessel group parameter in URL
    const urlParams = new URLSearchParams(window.location.search);
    const vesselGroupParam = urlParams.get('vessel_group');
    
    if (this.vesselGroupsArray && this.vesselGroupsArray.length > 0) {
      // If vessel group parameter exists, try to find and activate that group
      if (vesselGroupParam) {
        const targetGroupId = parseInt(vesselGroupParam);
        const targetGroup = this.vesselGroupsArray.find(group => group.id === targetGroupId);
        
        if (targetGroup) {
          this.activeGroupId = targetGroupId;
          
          // Scroll to the component after a short delay to ensure it's rendered
          this.$nextTick(() => {
            setTimeout(() => {
              this.scrollToComponent();
            }, 100);
          });
        } else {
          // Fallback to first group if specified group not found
          this.activeGroupId = this.vesselGroupsArray[0].id;
        }
      } else {
        // Set the first vessel group as active by default
        this.activeGroupId = this.vesselGroupsArray[0].id;
      }
    }
  },
  methods: {
    selectPackage(selectedVesselGroup) {
      // Handle package selection
    },
    togglePaymentPeriod() {
      // Handle payment period toggle
    },
    toggleVesselGroup(groupId) {
      // Only set as active if it's not already active (prevent deselecting)
      if (this.activeGroupId !== groupId) {
        this.activeGroupId = groupId;
      }
    },
    scrollToComponent() {
      // Try to find the component element by ID first
      let targetElement = document.getElementById('vessel-groups');
      
      // If no ID found, try to find by the component class
      if (!targetElement) {
        targetElement = document.querySelector('.vessel-groups-component');
      }
      
      // If still no element found, try to find the vessel-groups-block element
      if (!targetElement) {
        targetElement = document.querySelector('vessel-groups-block');
      }
      
      if (targetElement) {
        // Calculate offset to account for sticky headers or navigation
        const offset = 100; // Adjust this value based on your site's header height
        const elementPosition = targetElement.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    },
  }
};
</script>

<template>
  <div id="vessel-groups" class="vessel-groups-component">
    <div v-if="vesselGroupsArray && vesselGroupsArray.length > 0" class="vessel-groups-list__controls">
      <button 
        v-for="group in vesselGroupsArray" 
        :key="group.id" 
        class="vessel-group-item__toggle"
        :class="{ 'active': activeGroupId === group.id }"
        @click="toggleVesselGroup(group.id)">
        <h3 class="vessel-group-title">{{ group.title }}</h3>
      </button>
    </div>

    <div v-if="vesselGroupsArray && vesselGroupsArray.length > 0" class="vessel-groups__list">
      <div 
        v-for="group in vesselGroupsArray" 
        :key="group.id" 
        class="vessel-group-item"
        v-show="activeGroupId === group.id">

        <div class="vessel-group-item__image-wrapper">
          <div v-if="group.vessel_group_3d_image_url" class="vessel-group-item__image">
            <img 
              :src="group.vessel_group_3d_image_url" 
              :alt="group.vessel_group_3d_image_alt || group.title"
              :title="group.vessel_group_3d_image_title"
            />
          </div>
        
        </div>  

        <div class="vessel-group-item__content">

          <div class="vessel-group-item__description">
            <h3 class="vessel-group-title">{{ group.title }}</h3>
            
            <div v-if="group.excerpt" class="vessel-group-item__excerpt">
              <p>{{ group.excerpt }}</p>
            </div>
        
            <div v-if="group.vessel_group_intro_description" class="vessel-group-item__intro">
              <strong>{{ group.vessel_group_intro_description }}</strong>
            </div>
            <div v-if="group.vessel_group_description" class="vessel-group-item__description">
              <p>{{ group.vessel_group_description }}</p>
            </div>
            
            <div v-if="group.vessel_group_spec_list && group.vessel_group_spec_list.length > 0" class="vessel-group-item__specs">
              <h4>Specifications</h4>
              <ul class="vessel-specs-grid">
                <li v-for="(spec, specIndex) in group.vessel_group_spec_list" :key="specIndex" class="spec-row">
                  <div class="spec-label">{{ spec.key }}</div>
                  <div class="spec-value">{{ spec.value }}</div>
                </li>
              </ul>
            </div>
            
            <!-- Vessel List Section -->
            <div v-if="group.vessel_list && group.vessel_list.length > 0" class="vessel-group-list">
              <ul class="vessel-list-items">
                <li v-for="(listItem, itemIndex) in group.vessel_list" :key="itemIndex" class="vessel-list-item">
                  {{ listItem }}
                </li>
              </ul>
            </div>
          </div>
          
          <div v-if="group.vessel_group_related_vessels && group.vessel_group_related_vessels.length > 0" class="vessel-group-related">
            <div>
              <h4>Related Vessels</h4>
              <div class="related-vessels-grid">
                <div v-for="(vessel, vesselIndex) in group.vessel_group_related_vessels" :key="vesselIndex" class="related-vessel-item">
                  <!-- Debug: Show the entire vessel object
                  <div class="debug-info" style="font-size: 10px; color: red; margin-bottom: 10px;">
                    Debug: {{ JSON.stringify(vessel) }}
                  </div>
                  -->
                  
                  <a v-if="vessel && vessel.thumbnail_url" :href="vessel.permalink" class="related-vessel-image">
                    <img 
                      :src="vessel.thumbnail_url" 
                      :alt="vessel.title || 'Vessel image'"
                      :title="vessel.title"
                    />
                  </a>
                  <a v-else-if="vessel && vessel.vessel_post_object && vessel.vessel_post_object.ID" :href="vessel.permalink" class="related-vessel-image">
                    <img 
                      :src="`/wp-content/uploads/placeholder.jpg`" 
                      :alt="vessel.vessel_post_object.post_title || 'Vessel image'"
                      :title="vessel.vessel_post_object.post_title"
                      style="background-color: #f0f0f0;"
                    />
                  </a>
                  <div v-else class="no-image-placeholder">
                    <span>No image available</span>
                  </div>
                  
                  <div class="related-vessel-info">
                    <h5 v-if="vessel && vessel.title" class="related-vessel-title">{{ vessel.title }}</h5>
                    <h5 v-else-if="vessel && vessel.vessel_post_object && vessel.vessel_post_object.post_title" class="related-vessel-title">{{ vessel.vessel_post_object.post_title }}</h5>
                    <span v-else-if="typeof vessel === 'string'" class="related-vessel-text">{{ vessel }}</span>
                    <span v-else-if="vessel && vessel.ID" class="related-vessel-id">Vessel ID: {{ vessel.ID }}</span>
                    <span v-else-if="vessel && vessel.vessel_post_object && vessel.vessel_post_object.ID" class="related-vessel-id">Vessel ID: {{ vessel.vessel_post_object.ID }}</span>
                    <span v-else class="related-vessel-unknown">{{ vessel }}</span>
                    
                    <!-- Read More Link -->
                    <a v-if="vessel && vessel.permalink" 
                      :href="vessel.permalink" 
                      class="read-more-link"
                      >
                      Read More
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>

      

    </div>
    
    <div v-else class="no-vessel-groups">
      <p>No vessel groups available.</p>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .vessel-groups-list__controls{
    top: 50px;
    display: flex;
    gap: 40px;
    margin-bottom: 50px;
    position: sticky;
    z-index: 5;
    overflow-x: auto;
    @media (max-width: 800px){
      top: 20px;
      gap: 14px;
    }
    h3{
      margin-bottom: 0px;
    }
    .vessel-group-item__toggle{
      background-color: white;
      border: none;
      cursor: pointer;
      padding: 10px 20px;
      border-radius: 8px;
      transition: all 0.3s ease;
      white-space: nowrap;
      border-radius: 100px;
      @media (max-width: 800px){
        padding: 6px 14px;
      }
      h3{
        font-size: 18px;
        @media (max-width: 800px){
          font-size: 14px;
        }
      }
      
      &:hover {
        background-color: var(--wp--preset--color--secondary);
        color: white;
      }
      
      &.active {
        background-color: var(--wp--preset--color--primary);
        color: white;
        
        h3 {
          color: white;
        }
      }
    }
  }
  .vessel-group-item__image-wrapper{
    position: relative;
    width: 100%;
    height: 100%;
    aspect-ratio: 16 / 11;
  }
  .vessel-group-item__image-inner{
    position: absolute;
    z-index: 1;
    padding: 48px;
    max-width: 400px;
  }
  .vessel-group-item__image{
    position: absolute;
      width: 100%;
      height: 100%;
      z-index: 1;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
    img{
      height: 100%;
      width: 100%;
      object-fit: contain;

    }
  }
  .vessel-specs-grid{
    column-count: 2;
    max-width: 600px;
    .spec-row{
      display: grid;
      grid-template-columns: 1fr 1fr;
      max-width: 200px;
      .spec-label{
        font-weight: 600;
      }
    }
  }
  .vessel-group-item__content{
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--wp--preset--spacing--80);
    margin-top: 24px;
    @media (max-width: 1200px){
      grid-template-columns: 1fr;
    }
  }
  .vessel-group-item{
    margin-bottom: 100px;
    
    &__excerpt {
      margin: 15px 0;
      
      p {
        margin: 0;
        color: #666;
        font-size: 16px;
        line-height: 1.5;
        font-style: italic;
      }
    }
  }
  

  

  .vessel-group-related {

    .related-vessels-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 16px;
      margin-top: 15px;
    }

    .related-vessel-item {
      display: flex;
      flex-direction: column;
    }

    .related-vessel-image {
      width: 100%;
      aspect-ratio: 4 / 3;
      margin-bottom: 10px;
      overflow: hidden;
      border-radius: 4px;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
      }
    }

    .no-image-placeholder {
      width: 100%;
      max-width: 150px;
      aspect-ratio: 4 / 3;
      margin-bottom: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: #f0f0f0;
      border: 1px dashed #ccc;
      border-radius: 4px;
      font-size: 12px;
      color: #666;
    }

    .related-vessel-info {
      flex: 1;
      
      .related-vessel-title {
        margin: 0;
        font-size: 14px;
        font-weight: 600;
        color: #333;
        line-height: 1.3;
      }

      .related-vessel-text,
      .related-vessel-id,
      .related-vessel-unknown {
        font-size: 12px;
        color: #666;
      }

      .read-more-link {
        display: inline-block;
        margin-top: 8px;
        text-decoration: none;
        font-size: 15px;
        text-decoration: underline;
        color: #00389C;
      }
    }
  }

  // Vessel List Styles
  .vessel-group-list {
    margin-top: 30px;
    
    h4 {
      margin-bottom: 15px;
      font-size: 18px;
      font-weight: 600;
    }
    
    .vessel-list-items {
      list-style: none;
      padding: 0;
      margin: 0;
      
      .vessel-list-item {
        padding: 8px 0;
        border-bottom: 1px solid #e0e0e0;
        font-size: 14px;
        color: #555;
        line-height: 1.4;
        
        &:last-child {
          border-bottom: none;
        }
        
        &:before {
          content: "•";
          color: var(--wp--preset--color--primary);
          font-weight: bold;
          display: inline-block;
          width: 1em;
          margin-right: 8px;
        }
      }
    }
  }

</style>
